/** Minutes past midnight on the practice's wall clock, shared by the Schedule board and the calendar. */

export function toMinutes(time: string): number | undefined {
  // Tolerant of `9:05`, `09:05` and `09:05:00`, all of which appear across the
  // PMS exports. Anything else is a record we cannot place, and guessing a
  // position for it would draw a block at a time nobody booked.
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(time?.trim() ?? ``)
  if (!match) return undefined
  const hours = Number(match[1])
  const mins = Number(match[2])
  if (hours > 23 || mins > 59) return undefined
  return hours * 60 + mins
}

export function formatMinutes(minutes: number): string {
  const h24 = Math.floor(minutes / 60)
  const m = minutes % 60
  const suffix = h24 < 12 ? `AM` : `PM`
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12
  return `${h12}:${String(m).padStart(2, `0`)} ${suffix}`
}

/**
 * A span of the clock, as short as it can be said: "2 - 3 PM", the meridiem once when both ends
 * share it, because a column about a hundred pixels wide has no room for "2:00 PM - 3:00 PM".
 */
export function formatRange(start: number, end: number): string {
  const trim = (label: string) => label.replace(`:00`, ``)
  const from = trim(formatMinutes(start))
  const to = trim(formatMinutes(end))
  const [fromClock, fromMeridiem] = from.split(` `)
  const [, toMeridiem] = to.split(` `)
  return fromMeridiem === toMeridiem ? `${fromClock} - ${to}` : `${from} - ${to}`
}

/**
 * Spans packed side by side into as few lanes as their overlaps need: a double-booked chair shows
 * both visits. Lanes are counted per cluster of overlapping spans, not per day, so one nine
 * o'clock double booking does not draw the whole day at half width. Back to back is not an overlap.
 */
export function packLanes<T extends { start: number; end: number }>(
  spans: readonly T[],
): (T & { lane: number; lanes: number })[] {
  const placed: (T & { lane: number; lanes: number })[] = []
  let cluster: (T & { lane: number; lanes: number })[] = []
  let laneEnds: number[] = []
  let clusterEnd = -1
  const closeCluster = () => {
    const lanes = laneEnds.length || 1
    for (const span of cluster) placed.push({ ...span, lanes })
    cluster = []
    laneEnds = []
  }
  for (const span of [...spans].sort((a, b) => a.start - b.start || a.end - b.end)) {
    if (span.start >= clusterEnd && cluster.length) closeCluster()
    let lane = laneEnds.findIndex((end) => end <= span.start)
    if (lane === -1) lane = laneEnds.length
    laneEnds[lane] = span.end
    clusterEnd = Math.max(clusterEnd, span.end)
    cluster.push({ ...span, lane, lanes: 1 })
  }
  if (cluster.length) closeCluster()
  return placed
}
